// Imports
// React
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

// Components
import { CloseMenuIcon, DarkModeIcons } from '../../common/icons';

// Contexts
import { useDarkModeContext } from '../../../contexts/DarkModeContext';
import { useAuthContext } from '../../../contexts/AuthContext';

// Services
import { useFeatureFlag } from '../../../services/featureFlags/hooks/useFeatureFlag';

// Utils
import { createLogger } from '../../../utils/logger';

// MobileNavModal component
type MobileNavModalProps = {
  open: boolean;
  onClose: () => void;
  isAuthenticated: boolean;
  onSignOut: () => void;
  navigate: (path: string) => void;
  isDark: boolean;
  toggleDarkMode: () => void;
};

const MobileNavModal: React.FC<MobileNavModalProps> = ({
  open,
  onClose,
  isAuthenticated,
  onSignOut,
  navigate,
  isDark,
  toggleDarkMode,
}) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white text-black dark:bg-black dark:text-white">
      {/* Gradient definitions for icons */}
      <svg style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}>
        <defs>
          <linearGradient id="brand-gradient-sun" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ec4899" />
            <stop offset="100%" stopColor="#9333ea" />
          </linearGradient>
          <linearGradient id="brand-gradient-moon" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ec4899" />
            <stop offset="100%" stopColor="#9333ea" />
          </linearGradient>
        </defs>
      </svg>
      <button
        className="absolute right-6 top-6 focus:outline-none"
        onClick={onClose}
        aria-label="Close menu"
      >
        <CloseMenuIcon className="h-8 w-8" />
      </button>
      {isAuthenticated ? (
        <>
          <div className="flex flex-1 flex-col items-center justify-center space-y-8 text-4xl font-bold">
            <button
              onClick={() => {
                navigate('/');
                onClose();
              }}
              className="w-full px-8 py-2 text-left"
            >
              Home
            </button>
            <button
              onClick={() => {
                navigate('/calendar');
                onClose();
              }}
              className="w-full px-8 py-2 text-left"
            >
              Calendar
            </button>
            <button
              onClick={() => {
                navigate('/profile');
                onClose();
              }}
              className="w-full px-8 py-2 text-left"
            >
              Profile
            </button>
            <button
              onClick={toggleDarkMode}
              className="flex w-full items-center justify-between px-8 py-2 text-left"
              aria-label="Toggle dark mode"
            >
              <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>
              <DarkModeIcons isDark={isDark} className="h-8 w-8" />
            </button>
          </div>
          <div className="flex flex-col items-center pb-10">
            <button
              onClick={() => {
                onSignOut();
                onClose();
              }}
              className="w-64 rounded-xl border border-black bg-white py-4 text-xl font-semibold text-black transition hover:bg-black hover:text-white dark:border-white dark:bg-black dark:text-white dark:hover:bg-white dark:hover:text-black"
            >
              Sign out
            </button>
          </div>
        </>
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center">
          <button
            onClick={() => {
              navigate('/auth?mode=signIn');
              onClose();
            }}
            className="w-64 rounded-xl border border-white py-4 text-xl font-semibold transition hover:bg-white hover:text-black"
          >
            Sign in
          </button>
        </div>
      )}
    </div>
  );
};

// Constants
const logger = createLogger('NavigationBar');

// Component Definition
export const NavigationBar: React.FC = () => {
  // State Management
  const { isLoading: isFeatureFlagLoading, error: featureFlagError } =
    useFeatureFlag('enable-auth-nav');
  const { isAuthenticated, signOut } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, toggleDarkMode } = useDarkModeContext();

  // Scroll state for morphing navbar
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Define isLandingPage for background logic
  const isLandingPage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 48); // threshold for morphing
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (isFeatureFlagLoading || featureFlagError) {
    logger.info('Feature flag not ready', {
      isFeatureFlagLoading,
      featureFlagError,
    });
    return null;
  }

  // EventHandlers
  const handleSignOut = async () => {
    const result = await signOut();
    if (result.success) {
      navigate('/');
    }
  };

  // Dynamic classes for navbar
  const baseNavClass = 'z-50 flex items-center justify-between px-12 font-sans antialiased';

  // Conditional background colors based on page
  const getBackgroundClass = () => {
    if (isLandingPage) {
      // Landing page: white/black
      return isDark ? 'bg-black' : 'bg-white';
    } else {
      // Other pages: match calendar/profile page background
      return isDark ? 'bg-black' : 'bg-white';
    }
  };

  // Always use pill style, but add shadow when scrolled
  const pillClass =
    `fixed left-1/2 top-4 -translate-x-1/2 w-[94vw] rounded-xl ${getBackgroundClass()} border-0 backdrop-blur-lg py-4 px-4 ` +
    (isScrolled ? 'shadow-xl' : 'shadow-none');

  return (
    <>
      {/* Gradient definitions for icons */}
      <svg style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}>
        <defs>
          <linearGradient id="brand-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ec4899" />
            <stop offset="100%" stopColor="#9333ea" />
          </linearGradient>
          <linearGradient id="brand-gradient-profile" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ec4899" />
            <stop offset="100%" stopColor="#9333ea" />
          </linearGradient>
          <linearGradient id="brand-gradient-sun" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ec4899" />
            <stop offset="100%" stopColor="#9333ea" />
          </linearGradient>
          <linearGradient id="brand-gradient-moon" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ec4899" />
            <stop offset="100%" stopColor="#9333ea" />
          </linearGradient>
        </defs>
      </svg>
      {/* Desktop Navbar */}
      <nav
        className={baseNavClass + ' ' + pillClass + ' hidden md:flex'}
        style={{ transitionProperty: 'all' }}
      >
        {/* Left Section - Logo */}
        <div className="flex items-center justify-start" style={{ width: '33.33%' }}>
          <Link to="/" className="flex items-center space-x-2">
            <h1 className="bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-3xl font-black tracking-tight text-transparent antialiased">
              TunedCircuit
            </h1>
          </Link>
        </div>
        {/* Center Section */}
        <div className="flex items-center justify-center" style={{ width: '33.33%' }}></div>
        {/* Right Section - Empty for validation-only mode */}
        <div
          className="flex items-center justify-end space-x-2 md:space-x-4"
          style={{ width: '33.33%' }}
        >
          {/* Simplified for Mom Test validation - no distractions */}
        </div>
      </nav>
      {/* Mobile Navbar */}
      <nav
        className={
          'fixed left-1/2 top-4 z-50 flex h-12 w-[95vw] max-w-2xl -translate-x-1/2 items-center justify-between rounded-md bg-white px-4 dark:bg-black md:hidden ' +
          baseNavClass
        }
        style={{ transitionProperty: 'all', '--navbar-height': '48px' } as React.CSSProperties}
      >
        {/* Left Section - Logo, left-aligned */}
        <div className="flex h-full items-center">
          <Link to="/" className="flex h-full items-center">
            <h1 className="bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-2xl font-black tracking-tight text-transparent antialiased">
              TunedCircuit
            </h1>
          </Link>
        </div>
        {/* Right Section - Empty for validation-only mode */}
        <div className="flex h-full items-center space-x-2">
          {/* Simplified for Mom Test validation - no distractions */}
        </div>
      </nav>
      {/* Mobile Nav Modal */}
      <MobileNavModal
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        isAuthenticated={isAuthenticated}
        onSignOut={handleSignOut}
        navigate={navigate}
        isDark={isDark}
        toggleDarkMode={toggleDarkMode}
      />
    </>
  );
};
